import type { AgenticSettings } from "../api/agenticClient";

/** Mirror of the settings the server last confirmed. The Import tab paints before
 * its first fetch resolves, so without this the sync interval and toggles would
 * flash library defaults over whatever you had configured. */
const AGENTIC_SETTINGS_CACHE_KEY = "sfe-agentic-settings-v1";

/** Transient server-side fields (autoImportLastRunAt) stay out of the cache — a
 * stale timestamp is worse than none until the real settings land. */
export function readCachedAgenticSettings(fallback: AgenticSettings): AgenticSettings {
  try {
    const raw = localStorage.getItem(AGENTIC_SETTINGS_CACHE_KEY);
    const saved = raw ? (JSON.parse(raw) as Partial<AgenticSettings>) : null;
    if (!saved) return fallback;
    const minutes = Number(saved.snapshotAutoRefreshMinutes);
    return {
      ...fallback,
      snapshotAutoRefreshEnabled:
        typeof saved.snapshotAutoRefreshEnabled === "boolean"
          ? saved.snapshotAutoRefreshEnabled
          : fallback.snapshotAutoRefreshEnabled,
      snapshotAutoRefreshMinutes: Number.isFinite(minutes)
        ? Math.min(120, Math.max(1, Math.round(minutes)))
        : fallback.snapshotAutoRefreshMinutes,
      autoImportEnabled:
        typeof saved.autoImportEnabled === "boolean"
          ? saved.autoImportEnabled
          : fallback.autoImportEnabled,
    };
  } catch {
    return fallback;
  }
}

export function cacheAgenticSettings(settings: AgenticSettings): void {
  try {
    localStorage.setItem(
      AGENTIC_SETTINGS_CACHE_KEY,
      JSON.stringify({
        snapshotAutoRefreshEnabled: settings.snapshotAutoRefreshEnabled,
        snapshotAutoRefreshMinutes: settings.snapshotAutoRefreshMinutes,
        autoImportEnabled: settings.autoImportEnabled,
      }),
    );
  } catch {
    // Without persistence the panel still corrects itself once settings load.
  }
}
